import { Observable, observe, Scope } from 'wdc-cube'
import {
    ChartEmpresasAbertasFechadasScope,
    FilterCatalogScope,
    MapCardScope,
    TableAtividadeEconomicaScope,
    ValueScope,
    type AppliedFilterSummary,
    type IFilterScope
} from '../the-consumer-market/tcm_scopes'
import type { MercadoVetTab } from './mv_keys'
import type { ContatoRow, EmpresaRow, EstabelecimentoRow, PontoMapa } from './mv_types'

export { ChartEmpresasAbertasFechadasScope, TableAtividadeEconomicaScope, ValueScope }

export type Option = { value: string; label: string }

// ::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::
// :: Componentes compartilhados pelas abas
// ::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::

@Observable
export class SearchBarScope extends Scope {
    @observe() placeholder = ''
    @observe() texto = ''
    @observe() modo = ''
    @observe() modos = [] as Option[]

    onTextoChange = Scope.SYNC_ACTION_STRING
    onModoChange = Scope.ASYNC_ACTION_STRING
    onSubmit = Scope.ASYNC_ACTION
    onClear = Scope.ASYNC_ACTION
}

@Observable
export class AppliedFiltersScope extends Scope {
    @observe() filters = [] as AppliedFilterSummary[]
}

export type WordRow = { texto: string; peso: number; selected: boolean }

@Observable
export class WordCloudScope extends Scope {
    @observe() caption = ''
    @observe() loaded = false
    @observe() words = [] as WordRow[]

    onWordClicked = Scope.ASYNC_ACTION_STRING
}

@Observable
export class PointsMapScope extends Scope {
    @observe() caption = ''
    @observe() loaded = false
    @observe() points = [] as PontoMapa[]
    @observe() selectedId = ''

    onPointClicked?: (id: string) => Promise<void>
}

export type CategoriaTableRow = { id: string; categoria: string; total: number; selected?: boolean }

@Observable
export class CategoriaTableScope extends Scope {
    @observe() caption = ''
    @observe() loaded = false
    @observe() rows = [] as CategoriaTableRow[]
    @observe() total = 0

    onItemClicked = Scope.ASYNC_ACTION_STRING
}

export type SortModel = { field: string; desc: boolean }

export type EmpresasTableVariante = 'resumo' | 'completa'

export type ColumnVisibility = Record<string, boolean>

export interface PagedTable<T> {
    caption: string
    loaded: boolean
    loading: boolean
    rows: T[]
    total: number
    page: number
    pageSize: number
    sort: SortModel | undefined
    selectedId: string
    onPageChange: (page: number, pageSize: number) => Promise<void>
    onSortChange: (sort: SortModel | undefined) => Promise<void>
    onRowClicked: (id: string) => Promise<void>
}

@Observable
export class EmpresasTableScope extends Scope implements PagedTable<EmpresaRow> {
    @observe() caption = ''
    @observe() loaded = false
    @observe() loading = false
    @observe() rows = [] as EmpresaRow[]
    @observe() total = 0
    @observe() page = 0
    @observe() pageSize = 50
    @observe() sort: SortModel | undefined = undefined
    @observe() selectedId = ''
    @observe() variante: EmpresasTableVariante = 'resumo'
    @observe() columnVisibility: ColumnVisibility = {}

    onPageChange = Scope.ASYNC_ACTION_TWO<number, number>()
    onSortChange = Scope.ASYNC_ACTION_ONE<SortModel | undefined>()
    onRowClicked = Scope.ASYNC_ACTION_STRING
    onColumnVisibilityChange = Scope.ASYNC_ACTION_ONE<ColumnVisibility>()
    @observe() expanded = false
    onExpandClick?: () => Promise<void>
    onShrunkClick?: () => Promise<void>
}

@Observable
export class EstabelecimentosTableScope extends Scope implements PagedTable<EstabelecimentoRow> {
    @observe() caption = ''
    @observe() loaded = false
    @observe() loading = false
    @observe() rows = [] as EstabelecimentoRow[]
    @observe() total = 0
    @observe() page = 0
    @observe() pageSize = 50
    @observe() sort: SortModel | undefined = undefined
    @observe() selectedId = ''

    onPageChange = Scope.ASYNC_ACTION_TWO<number, number>()
    onSortChange = Scope.ASYNC_ACTION_ONE<SortModel | undefined>()
    onRowClicked = Scope.ASYNC_ACTION_STRING
}

// ::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::
// :: Filtros
// ::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::

@Observable
export class MvFilterPaneScope extends Scope {
    readonly catalog = new FilterCatalogScope()
    @observe() catalogVisible = false

    @observe() expanded = false
    @observe() isFiltering = false
    @observe() searchFilterText = ''
    @observe() filters = [] as IFilterScope[]

    onToggleExpanded = Scope.ASYNC_ACTION
    onClearFilters = Scope.ASYNC_ACTION
    onApply = Scope.ASYNC_ACTION
    onSearchFilterChanged = Scope.ASYNC_ACTION_STRING
}

// ::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::
// :: Abas
// ::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::

@Observable
export class CnpjPageScope extends Scope {
    readonly busca = new SearchBarScope()
    readonly filtros = new MvFilterPaneScope()

    readonly appliedFilters = new AppliedFiltersScope()

    readonly totalEmpresas = new ValueScope()
    readonly totalAtivas = new ValueScope()
    readonly totalFuncionarios = new ValueScope()
    readonly secundarias = new TableAtividadeEconomicaScope()
    readonly nuvem = new WordCloudScope()
    readonly mapa = new PointsMapScope()
    readonly mapaFerramentas = new MapCardScope()
    readonly detalhamento = new EmpresasTableScope()

    readonly mercadoEmpresas = new ValueScope()
    readonly mercadoAtivas = new ValueScope()
    readonly mercadoNovas = new ValueScope()
    readonly novasEmpresas = new ChartEmpresasAbertasFechadasScope()

    @observe() detalhar: EmpresasTableScope | null = null
    @observe() erro = ''
}

@Observable
export class FotoScope extends Scope {
    @observe() loaded = false
    @observe() estabelecimento: EstabelecimentoRow | null = null
    @observe() fotoFalhou = false

    onFotoErro = Scope.SYNC_ACTION
}

@Observable
export class WebPageScope extends Scope {
    readonly busca = new SearchBarScope()
    readonly filtros = new MvFilterPaneScope()

    readonly appliedFilters = new AppliedFiltersScope()

    readonly totalEstabelecimentos = new ValueScope()
    readonly totalAbertas24h = new ValueScope()
    readonly totalFotos = new ValueScope()
    readonly categorias = new CategoriaTableScope()
    readonly nuvem = new WordCloudScope()
    readonly mapa = new PointsMapScope()
    readonly detalhamento = new EstabelecimentosTableScope()
    readonly foto = new FotoScope()

    @observe() erro = ''
}

@Observable
export class LinkedInPageScope extends Scope {
    @observe() loaded = false
    @observe() loading = false
    @observe() rows = [] as ContatoRow[]
    @observe() total = 0
    @observe() empresasComContato = 0
    @observe() filtro = ''
    @observe() erro = ''

    readonly busca = new SearchBarScope()
}

@Observable
export class HeaderScope extends Scope {
    @observe() busca: SearchBarScope | null = null
    @observe() buscaAba = ''
}

@Observable
export class MercadoVetScope extends Scope {
    readonly header = new HeaderScope()

    @observe() tab: MercadoVetTab = 'cnpj'
    @observe() atualizadoEm = ''
    @observe() dataDeCorte = ''
    @observe() indice = ''

    readonly cnpj = new CnpjPageScope()
    readonly web = new WebPageScope()
    readonly linkedin = new LinkedInPageScope()

    onTabChange = Scope.ASYNC_ACTION_ONE<MercadoVetTab>()
}

export default MercadoVetScope
